import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Modal as AntdModal } from 'antd'
import cl from 'classnames'
import { observer } from 'mobx-react'

import { Icon } from 'Shared/atoms'
import useBasicStores from 'Shared/hooks/useBasicStores'

import styles from './styles.module.scss'

export const FORM_MODAL_WIDTH = {
	XXXXLARGE: 1232,
	XXXLARGE: 1062,
	XXLARGE: 846,
	XLARGE: 720,
	LARGE: 510,
	MEDIUM: 628,
	SMALL: 410,
	SMALL2: 430,
	SMALL3: 450,
	XSMALL: 320
}

interface ModalInterface extends React.FC {
	ModalBody: React.FC<ModalBodyProps>
	ModalFooter: React.FC<ModalFooterProps>
}

interface ModalBodyProps {
	hasPadding?: boolean
	className?: string
	isMutationOverflow?: boolean
	moodalRef?: React.MutableRefObject<HTMLDivElement | null>
}

interface ModalFooterProps {
	className?: string
}

const ModalBody: React.FC<ModalBodyProps> = ({
	hasPadding = true,
	className,
	children,
	moodalRef
}) => {
	return (
		<div
			ref={moodalRef}
			className={cl(className, styles.modalBody, { [styles.modalBodyPadding]: hasPadding })}
		>
			{children}
		</div>
	)
}

const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => {
	return <div className={cl(className, styles.modalFooter)}>{children}</div>
}

const Modal: ModalInterface = () => {
	const {
		uiStore: { isModalOpened, closeModal, modalProps }
	} = useBasicStores()

	const history = useHistory()

	useEffect(() => {
		const unlisten = history.listen(() => {
			if (history.action === 'POP' && isModalOpened) {
				closeModal()
			}
		})
		return unlisten
	}, [closeModal, history, isModalOpened])

	if (!modalProps?.component || !isModalOpened) {
		return null
	}

	const { title, width = FORM_MODAL_WIDTH.SMALL, closable, className } = modalProps

	const ModalContent = modalProps.component

	return (
		<AntdModal
			title={title}
			open={isModalOpened}
			onCancel={closeModal}
			width={width}
			closable={closable}
			centered
			wrapClassName={styles.modalLayout}
			className={cl(styles.modal, className)}
			footer={null}
			maskClosable={false}
			closeIcon={<Icon type="close" />}
		>
			<ModalContent {...modalProps} closeModal={closeModal} />
		</AntdModal>
	)
}

Modal.ModalBody = ModalBody
Modal.ModalFooter = ModalFooter

export default observer(Modal)
